import { useMemo } from 'react';
import { Chip, Divider, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from '../../../config/dayjs';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSION } from '../../../shared/utils/permissions';
import { useKbSourcesQuery, useToggleSource } from '../../../hooks/useSupport';
import type { KbSource } from '../../../types/support';
import {
  DataTable,
  DataTableSkeleton,
  type DataTableColumnMeta,
  type DataTableRenderers,
} from '../components/DataTable';
import { EmptyState } from '../../../components/common/EmptyState/EmptyState';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';
import { AddTrainingTextForm } from './AddTrainingTextForm';
import { TestRetrievalPanel } from './TestRetrievalPanel';
import { kbSourceTypeLabelKey } from './kbMeta';
import { SectionTabs } from '../components/SectionTabs';
import { SUPPORT_SECTION } from '../../../layouts/DashboardV2Layout/dashboardSections';

const KB_COLUMNS = [
  { key: 'type', labelKey: 'dashboard.tables.kb.type', width: '15%', skeletonVariant: 'id' },
  { key: 'name', labelKey: 'dashboard.tables.kb.name', width: '30%', cellClassName: 'cell-id', skeletonVariant: 'address' },
  { key: 'chunkCount', labelKey: 'dashboard.tables.kb.chunkCount', width: '13%', align: 'center' },
  { key: 'lastIngested', labelKey: 'dashboard.tables.kb.lastIngested', width: '17%', skeletonVariant: 'id' },
  { key: 'error', labelKey: 'dashboard.tables.kb.error', width: '15%' },
  { key: 'enabled', labelKey: 'dashboard.tables.kb.enabled', width: '10%', align: 'center' },
] as const satisfies readonly DataTableColumnMeta[];

const KB_TABLE_MIN_WIDTH = '960px';

/**
 * The knowledge base's control surface: what the assistant knows (the
 * sources table, with an enable/disable switch that hides a source from
 * retrieval without deleting it), a form to paste training text that gets
 * embedded on save, and a retrieval tester so an agent can see exactly why
 * the bot answered — or didn't — the way it did.
 */
export const KnowledgeBase = () => {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const canManage = has(PERMISSION.SUPPORT_MANAGE);

  const { data: sources, isLoading, isError, refetch } = useKbSourcesQuery();
  const toggleMutation = useToggleSource();

  const items = sources ?? [];

  const renderers = useMemo<DataTableRenderers<KbSource, typeof KB_COLUMNS>>(
    () => ({
      type: (source) => t(kbSourceTypeLabelKey(source.type)),
      name: (source) => source.name,
      chunkCount: (source) => source.chunkCount,
      lastIngested: (source) =>
        source.lastIngestedAt ? dayjs(source.lastIngestedAt).fromNow() : t('dashboard.kb.never'),
      error: (source) =>
        source.lastError ? (
          <Typography variant="body2" color="error">
            {source.lastError}
          </Typography>
        ) : (
          '-'
        ),
      enabled: (source) =>
        canManage ? (
          <Switch
            size="small"
            checked={source.enabled}
            disabled={
              toggleMutation.isPending && toggleMutation.variables?.id === source._id
            }
            onChange={(event) =>
              toggleMutation.mutate({ id: source._id, enabled: event.target.checked })
            }
            inputProps={{ 'aria-label': t('dashboard.kb.toggleSource', { name: source.name }) }}
          />
        ) : (
          <Chip
            size="small"
            color={source.enabled ? 'success' : 'default'}
            label={t(source.enabled ? 'dashboard.kb.enabled' : 'dashboard.kb.disabled')}
          />
        ),
    }),
    [t, canManage, toggleMutation],
  );

  // The page title is already rendered by the dashboard header (navConfig),
  // so the body starts at the sources table like the other list pages.
  return (
    <>
      <SectionTabs section={SUPPORT_SECTION} row />
      <div className="content-inner" style={{ padding: '2.5rem' }}>
        {isError ? (
          <ErrorMessage message={t('dashboard.kb.errorLoading')} onRetry={() => void refetch()} />
        ) : isLoading ? (
          <DataTableSkeleton columns={KB_COLUMNS} minWidth={KB_TABLE_MIN_WIDTH} />
        ) : items.length === 0 ? (
          <EmptyState icon="menu_book" message={t('dashboard.kb.empty')} />
        ) : (
          <DataTable
            columns={KB_COLUMNS}
            renderers={renderers}
            rows={items}
            rowKey={(source) => source._id}
            minWidth={KB_TABLE_MIN_WIDTH}
          />
        )}

        {canManage && (
          <>
            <Divider sx={{ my: 4 }} />
            <AddTrainingTextForm />
          </>
        )}

        <Divider sx={{ my: 4 }} />
        <TestRetrievalPanel />
      </div>
    </>
  );
};

export default KnowledgeBase;
